import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewRef,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSelectChange, MatSelect } from '@angular/material/select';
import { FormCanDeactivate } from '@extensions/form-can-deactivate';
import { ActivatedRoute, Router } from '@angular/router';
import { TypeSelection } from '@components/type-selector/1.0';

import { ButtonStyle } from '@constants/button/1.0';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { MessageType } from '@constants/message/1.0';
import { Tenant } from '@generated/tcea/models';
import { Country } from '@interfaces/country/1.0';
import { Currency } from '@interfaces/currency/1.0';
import { Language } from '@interfaces/language/1.0';
import { BrandService } from '@services/brand/1.0/brand.service';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { of, Subject } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import { LangChangeEvent, TranslateService, TranslateModule } from '@ngx-translate/core';
import { requiredIfValidator } from '@helpers/Validators';
import { environment } from 'environments/environment';
import { ConfirmationComponent, ConfirmationDialogData } from '@components/confirmation/1.0';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TceLocaleService } from '@services/tce-locale/1.0/tce-locale.service';
import { GetService } from '@services/utilities/1.0/get.service';
import { IsEmptyService } from '@services/utilities/1.0/is-empty.service';
import { UtilitiesService } from '@services/utilities/1.0/utilities.service';
import { brandMixin } from '@app/brand/mixins/brand-mixin';
import { NgIf, NgClass, NgFor } from '@angular/common';
import { LoadingComponent } from '@components/loading/1.0/loading.component';
import { MessageComponent } from '@components/message/1.0/message.component';
import { MatFormField, MatLabel, MatSuffix, MatError, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { TypeSelectorComponent } from '@components/type-selector/1.0/type-selector.component';
import { ButtonComponent } from '@components/button/1.0/button.component';

const ALIAS_NOT_UNIQUE_ERROR_CODE = '010001';

@Component({
  selector: 'txp-edit-business-info',
  templateUrl: './edit-business-info.component.html',
  styleUrls: ['./edit-business-info.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    LoadingComponent,
    MessageComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatFormField,
    MatLabel,
    MatInput,
    MatSuffix,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatHint,
    MatCheckbox,
    TypeSelectorComponent,
    ButtonComponent,
    TranslateModule,
  ],
})
export class EditBusinessInfoComponent
  extends brandMixin(FormCanDeactivate)
  implements OnInit, OnDestroy
{
  @Input() isCreate?: boolean;
  @Output() closeEvent: EventEmitter<any> = new EventEmitter<any>();
  loadingColor = LoadingColor; // expose enum to template
  loadingType = LoadingType; // expose enum to template
  buttonStyle = ButtonStyle; // expose enum to template
  messageType = MessageType; // expose enum to template

  isProduction = environment.production;
  activeBrand: Tenant;
  brandId: string;
  form: UntypedFormGroup;
  isLoading = false;
  isSaving = false;
  originalFormData: Tenant;
  showError = false;
  showDataLoadError = false;
  countries: Country[];
  filteredCountries: Country[];
  currencies: Currency[];
  filteredCurrencies: Currency[];
  languages: Language[];
  supportedLanguages: Language[];
  filteredLanguages: Language[];
  businessInfoReadOnly = true;
  unitsOfDistanceMeasure: TypeSelection[];
  createSandBox = false;
  dialogRef: MatDialogRef<ConfirmationComponent>;
  inputFocused = false;
  languageFilterText = '';
  aliasSuggestions: string[] = [];

  @ViewChild('supportedLanguageFilterInput', { static: false })
  supportedLanguageFilterInput: ElementRef<HTMLInputElement>;

  formControl: UntypedFormControl = new UntypedFormControl('', [Validators.required]);

  private unsubscribe = new Subject<void>();

  constructor(
    private brandService: BrandService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private translateService: TranslateService,
    private permissionsService: PermissionsService,
    private tceLocaleService: TceLocaleService,
    private utilitiesService: UtilitiesService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.businessInfoReadOnly = !this.permissionsService.getPermission('tenant:write');
    this.setForm();

    this.unitsOfDistanceMeasure = [
      { data: 'mi', label: this.translateService.instant('brand.businessInfo.mi') },
      { data: 'km', label: this.translateService.instant('brand.businessInfo.km') },
    ];

    this.businessInfoReadOnly ? this.form.disable() : this.form.enable();
  }

  setForm(): void {
    if (this.createSandBox) {
      // Minimal Form (Only Name & Alias)
      this.form = this.formBuilder.group({
        name: new UntypedFormControl('', Validators.required),
        countryCode: new UntypedFormControl({ value: 'US' }),
        alias: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern('^[A-Z]{2,5}$'),
        ]),
        email: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
        ]),
      });
    } else {
      // Full Form
      this.form = this.formBuilder.group(
        {
          name: new UntypedFormControl('', Validators.required),
          countryCode: new UntypedFormControl('', Validators.required),
          alias: new UntypedFormControl('', [
            Validators.required,
            Validators.pattern('^[A-Z]{2,5}$'),
          ]),
          currency: new UntypedFormControl('', Validators.required),
          supportedLanguages: new UntypedFormControl('', Validators.required),
          language: new UntypedFormControl('', Validators.required),
          unitOfDistanceMeasure: new UntypedFormControl('mi'),
          headquartersAddress: this.formBuilder.group({
            coords: this.formBuilder.group({
              lat: new UntypedFormControl(0, [
                Validators.min(-90),
                Validators.max(90),
                Validators.pattern(/^-?[0-9]{1,2}(?:\.[0-9]{1,6})?$/),
              ]),
              lon: new UntypedFormControl(0, [
                Validators.min(-180),
                Validators.max(180),
                Validators.pattern(/^-?[0-9]{1,3}(?:\.[0-9]{1,6})?$/),
              ]),
            }),
          }),
        },
        { validator: customValidator },
      );

      const latControl = this.form.get('headquartersAddress.coords.lat');
      const lonControl = this.form.get('headquartersAddress.coords.lon');

      // setup validators that depend on other
      latControl.setValidators([
        latControl.validator,
        requiredIfValidator(() => typeof lonControl.value === 'number'),
      ]);
      lonControl.setValidators([
        lonControl.validator,
        requiredIfValidator(() => typeof latControl.value === 'number'),
      ]);
    }
    this.isLoading = false;
  }

  ngOnInit() {
    this.loadLocaleData();

    const brandId = sessionStorage.getItem('brand_id');

    this.translateService.onLangChange.subscribe((e: LangChangeEvent) => {
      this.unitsOfDistanceMeasure = [
        { data: 'mi', label: this.translateService.instant('brand.businessInfo.mi') },
        { data: 'km', label: this.translateService.instant('brand.businessInfo.km') },
      ];
    });

    // Subscribe to name form control valueChanges to regenerate suggestions
    this.form
      .get('name')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.generateAliasSuggestions();
      });

    if (!this.isCreate) {
      this.brandService.currentBrandBehaviorSubject
        .pipe(
          filter((brand) => brand?.isSet),
          takeUntil(this.unsubscribe),
        )
        .subscribe((brand) => {
          if (this.brandId !== brand.id) {
            this.router.navigateByUrl(`brand/${brand.id}/info`);
          }
          if (brand.isSet) {
            this.activeBrand = brand;
            this.brandId = this.activeBrand.id;
            this.loadActiveBrand(this.activeBrand);
          }
        });
    } else {
      this.isLoading = false;
    }

    if (!this.activeBrand && this.route.parent) {
      this.route.parent.params.subscribe((params) => {
        if (!brandId) {
          this.router.navigateByUrl('brand');
          return;
        }
        this.brandId = params.id;
        this.getBrand(this.brandId);
      });
    }
  }

  loadLocaleData() {
    this.tceLocaleService
      .getLocaleData()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (localeData) => {
          this.countries = localeData.countries;
          this.filteredCountries = [...this.countries];

          this.currencies = localeData.currencies;
          this.filteredCurrencies = [...localeData.currencies];

          this.languages = localeData.languages;
          this.supportedLanguages = [...localeData.languages];
          this.filteredLanguages = [...localeData.languages];
        },
        error: () => {
          this.showDataLoadError = true;
        },
      });
  }

  filterItems(searchText: string, list: any[], key: string): any[] {
    if (!searchText) {
      return list;
    }
    return list.filter((item) => item[key]?.toLowerCase()?.includes(searchText.toLowerCase()));
  }

  filterLanguagesWithSelected(searchText: string): void {
    const selectedLanguageCodes = this.form.get('supportedLanguages').value || [];

    if (!searchText) {
      this.filteredLanguages = [...this.languages];
      return;
    }

    const searchLower = searchText.toLowerCase();
    // Filter languages based on search text
    const matchingLanguages = this.languages.filter((lang) =>
      lang.languageName?.toLowerCase()?.includes(searchLower),
    );

    // Always include selected languages so they remain visible and maintain selection state
    // This ensures selected languages don't disappear when filtering
    const selectedLanguages = this.languages.filter((lang) =>
      selectedLanguageCodes.includes(lang.languageCode),
    );

    // Combine matching and selected languages, removing duplicates
    const combined = [...matchingLanguages];
    selectedLanguages.forEach((selectedLang) => {
      if (!combined.find((lang) => lang.languageCode === selectedLang.languageCode)) {
        combined.push(selectedLang);
      }
    });

    this.filteredLanguages = combined;
  }

  resetLanguageFilter(): void {
    this.filteredLanguages = [...this.languages];
    this.languageFilterText = '';
    // Clear the input value when panel closes
    if (this.supportedLanguageFilterInput?.nativeElement) {
      this.supportedLanguageFilterInput.nativeElement.value = '';
    }
  }

  onLanguagesPanelOpened(): void {
    // When panel opens, show all languages but don't clear the filter text
    // This allows user to continue typing if they want
    this.filteredLanguages = [...this.languages];
  }

  onLanguagesPanelClosed(): void {
    // When panel closes, reset everything
    this.resetLanguageFilter();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  canDeactivate(): boolean {
    if (this.form) {
      return this.form.disabled || !this.form.dirty;
    } else {
      return true;
    }
  }

  dataLoading() {
    return this.isLoading || this.brandService.isLoadingCurrent;
  }

  setupFormData(data: Tenant) {
    const values = [
      'name',
      'countryCode',
      'alias',
      'currency',
      'supportedLanguages',
      'language',
      'unitOfDistanceMeasure',
      'headquartersAddress',
    ];
    const defaultValueMap = {
      headquartersAddress: {
        coords: {
          lat: 0,
          lon: 0,
        },
      },
    };

    values.forEach((v) => {
      const dataToSet = IsEmptyService.isEmpty(data[v]) ? defaultValueMap[v] : data[v];

      this.form.get(v).setValue(dataToSet);
    });
    this.filterSupportedLanguages();
    // Generate suggestions if both name and countryCode are available
    this.generateAliasSuggestions();
  }

  onSave() {
    this.showError = false;
    if (this.form.errors?.lang) {
      this.form.get('language').setErrors({ lang: true });
      this.showError = true;
    }
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.form.valueChanges.pipe(first()).subscribe(() => {
        this.showError = false;
      });
      if (!!this.isCreate) {
        this.create(this.form);
      } else {
        this.save(this.form);
      }
    } else {
      this.utilitiesService.findInvalidControls(this.form);
      this.showError = true;
    }
  }

  onCancel() {
    this.closeEvent.emit();
    if (!this.isCreate) {
      // needed in order to clear the requiredIfValidators properly
      this.form.reset(this.originalFormData);
      this.setupFormData(this.originalFormData);
      this.showError = false;
      this.form.markAsPristine();
    }
  }

  onEdit() {
    this.showError = false;
  }

  onSelectCountry({ value }: MatSelectChange) {
    this.onSelectUnitOfDistanceMeasure(value === 'US' ? 'mi' : 'km');
  }

  onSelectUnitOfDistanceMeasure(data: string) {
    const control = this.form.get('unitOfDistanceMeasure');
    control.setValue(data);
    control.markAsDirty();
  }

  /**
   * Returns dirty keys from Form Control
   *
   * @param form - Form which is read on submit//save
   * @returns - string[] of dirty form keys
   */
  getDirtyBrand(form: UntypedFormGroup): Partial<Tenant> {
    if (this.createSandBox) {
      return form.value;
    }
    const dirtyObject: Partial<Tenant> = {};
    let currentControl: AbstractControl;
    Object.keys(form.controls).forEach((name) => {
      currentControl = form.controls[name];
      if (currentControl.dirty) {
        dirtyObject[name] = form.controls[name].value;
      }
    });
    return dirtyObject;
  }

  setBrand(brand: Tenant) {
    this.brandId = brand.id;
    this.originalFormData = brand;
    this.setupFormData(brand);
  }

  // API Actions
  getBrand(id: string) {
    if (GetService.get(this.brandService, 'currentBrand.id') === id) {
      return this.setBrand(this.brandService.currentBrand);
    }
    this.isLoading = true;
    if (this.brandService.isLoadingCurrent) {
      return;
    }
    return this.brandService
      .getOne(id)
      .pipe(first())
      .subscribe({
        next: (brand) => {
          this.setBrand(brand);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.showDataLoadError = true;
        },
      });
  }

  loadActiveBrand(brand) {
    if (brand) {
      this.brandId = brand.id;
      this.originalFormData = brand;
      this.setupFormData(brand);
      this.isLoading = false;
    }
  }

  save(form) {
    const brandDirty: Partial<Tenant> = this.getDirtyBrand(form);
    const currentBrand: Tenant = this.originalFormData;
    const currentBrandId = currentBrand.id;

    this.isSaving = true;
    this.saveBrand(brandDirty, currentBrandId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => {
          this.isSaving = false;
          // copy changed data onto original
          this.originalFormData = Object.assign({}, this.originalFormData, brandDirty);
          this.brandService.setCurrentContext(this.originalFormData as Tenant);
          this.isSaving = false;
          this.form.markAsPristine();
        },
        complete: () => {
          this.isSaving = false;
          this.form.markAsPristine();
        },
        error: this.handleSaveError,
      });
  }

  saveBrand(brandDirty, currentBrandId) {
    delete brandDirty.images;
    return Object.keys(brandDirty)?.length
      ? this.brandService.save(brandDirty, currentBrandId).pipe(first())
      : of([]);
  }

  create(form) {
    const newBrand: Partial<Tenant> = this.getDirtyBrand(form);
    newBrand.schedule = [];

    // For ResellerProductOperations users, set resellerId from sessionStorage
    const isResellerProductOperations = this.permissionsService.getRole(
      'ResellerProductOperations',
    );
    if (isResellerProductOperations) {
      const resellerId = sessionStorage.getItem('reseller_id');
      if (resellerId) {
        newBrand.resellerId = resellerId;
      }
    }

    this.isSaving = true;
    this.brandService
      .create(newBrand, this.createSandBox)
      .pipe(first(), takeUntil(this.unsubscribe))
      .subscribe({
        next: (brand) => {
          if (this.createSandBox) {
            this.closeEvent.emit(true);
            this.onCreateSandbox();
            this.isSaving = false;
          } else {
            this.form.markAsPristine();
            this.brandService.setCurrentContext(brand);
            localStorage.setItem('active_tenant_identifier', brand.id);

            if (isResellerProductOperations) {
              // Wait 1.5 seconds for Auth0 to sync permissions before closing and reloading
              setTimeout(() => {
                this.closeEvent.emit(true);
                this.isSaving = false;
                this.brandService.reloadWithNewBrand(brand.id, `brand/${brand.id}/info`);
              }, 1500);
            } else {
              this.closeEvent.emit(true);
              this.isSaving = false;
              this.brandService.reloadWithNewBrand(brand.id, `brand/${brand.id}/info`);
            }
          }
        },
        error: this.handleSaveError,
      });
  }

  handleSaveError = (e: any) => {
    this.isSaving = false;

    const parameterErrors = this.getParameterErrors(e);
    // Alias uniqueness is scoped to country. The API reports both alias and
    // countryCode as "not unique" for that composite key. Country is not unique
    // across tenants, so only the alias field is marked invalid.
    const aliasError = parameterErrors.find(
      (err) => err?.parameterName === 'alias' && err?.errorCode === ALIAS_NOT_UNIQUE_ERROR_CODE,
    );

    if (aliasError) {
      const aliasControl = this.form.get('alias');
      aliasControl?.setErrors({ notUnique: true });
      aliasControl?.markAsTouched();
      aliasControl?.markAsDirty();
    }

    this.showError = true;
    if (!(this.changeDetectorRef as ViewRef).destroyed) {
      this.changeDetectorRef.detectChanges();
    }
  };

  private getParameterErrors(error: any): { parameterName?: string; errorCode?: string }[] {
    const sources = [error, error?.error, error?.error?.error];

    for (const source of sources) {
      let body = source;
      if (typeof body === 'string') {
        try {
          body = JSON.parse(body);
        } catch {
          continue;
        }
      }

      const parameterErrors = body?.parameterErrors ?? body?._embedded?.parameterErrors;
      if (Array.isArray(parameterErrors)) {
        return parameterErrors;
      }
    }

    return [];
  }

  onCreateSandbox() {
    const modalContent: ConfirmationDialogData = {
      title: this.translateService.instant('brand.businessInfo.createSandbox'),
      message: this.translateService.instant('brand.businessInfo.createSandboxSuccessMessage'),
      confirmText: this.translateService.instant('form.button.ok'),
    };

    const dialogRef = this.dialog.open(ConfirmationComponent, {
      data: modalContent,
    });

    dialogRef.afterClosed().subscribe((confirmAction) => {
      if (confirmAction) {
        this.brandService.clearCurrentContext();
        this.closeEvent.emit(true);
        this.router.navigate(['/brand']);
      }
    });
  }

  onSupportedLanguagesChange({ value }: MatSelectChange) {
    const defaultLanguage = this.form.get('language').value;
    this.filterSupportedLanguages();
    // Update filtered languages to include newly selected languages
    if (this.languageFilterText) {
      this.filterLanguagesWithSelected(this.languageFilterText);
    }
    if (!value.includes(defaultLanguage)) {
      this.form.get('language').setValue(null);
      this.form.get('language').setErrors({ lang: true });
      this.form.setErrors({ lang: true });
    }
  }

  filterSupportedLanguages() {
    const supportedLanguages = this.form.get('supportedLanguages').value || [];
    this.supportedLanguages = this.languages.filter((lang) =>
      supportedLanguages.includes(lang.languageCode),
    );
  }

  onCreateSandBoxChecked(checked: boolean): void {
    this.createSandBox = checked;
    const formValues = this.form.getRawValue();

    this.setForm();
    this.form.patchValue(formValues);

    const countryControl = this.form.get('countryCode');

    if (this.createSandBox) {
      countryControl.setValue('US');
      countryControl.disable();
    } else {
      countryControl.enable();
    }

    this.form.markAsDirty();
  }

  generateAliasSuggestions(): void {
    const brandName = this.form.get('name')?.value?.trim() || '';

    if (!brandName) {
      this.aliasSuggestions = [];
      return;
    }

    const suggestions: string[] = [];
    const words = brandName
      .toUpperCase()
      .split(/\s+/)
      .filter((w) => w.length > 0);

    // Priority Strategy: Multi-word combinations (when brand has 2+ words)
    if (words.length >= 2) {
      const firstWord = words[0];
      const secondWord = words[1];

      // Generate combinations taking prefixes from first and second word
      // Examples: "Burger Paradise" → "BUPAR", "BPAR", "BURPA", "BURPAR"

      // Strategy 1a: First letter of first word + prefix of second word (prioritize longer)
      if (secondWord.length >= 4) {
        // B + PARA (5 chars)
        suggestions.push(firstWord[0] + secondWord.substring(0, 4));
      }
      if (secondWord.length >= 3) {
        // B + PAR (4 chars)
        suggestions.push(firstWord[0] + secondWord.substring(0, 3));
      }
      if (secondWord.length >= 2) {
        // B + PA (3 chars)
        suggestions.push(firstWord[0] + secondWord.substring(0, 2));
      }

      // Strategy 1b: Prefix of first word + prefix of second word
      // BUR + PA (5 chars)
      if (firstWord.length >= 3 && secondWord.length >= 2) {
        suggestions.push(firstWord.substring(0, 3) + secondWord.substring(0, 2));
      }
      // BU + PAR (5 chars)
      if (firstWord.length >= 2 && secondWord.length >= 3) {
        suggestions.push(firstWord.substring(0, 2) + secondWord.substring(0, 3));
      }
      // BU + PA (4 chars)
      if (firstWord.length >= 2 && secondWord.length >= 2) {
        const combo = firstWord.substring(0, 2) + secondWord.substring(0, 2);
        if (combo.length >= 3 && combo.length <= 5) {
          suggestions.push(combo);
        }
      }

      // Strategy 1d: If there's a third word, try combinations with it
      if (words.length >= 3 && suggestions.length < 5) {
        const thirdWord = words[2];
        // First letter of first + first letter of second + prefix of third
        if (thirdWord.length >= 2) {
          const combo = firstWord[0] + secondWord[0] + thirdWord.substring(0, 2);
          if (combo.length >= 3 && combo.length <= 5) {
            suggestions.push(combo);
          }
        }
      }
    }

    // Fallback Strategy: Single-word prefixes (only if we don't have enough multi-word suggestions)
    if (suggestions.length < 5 && words[0] && words[0].length >= 3) {
      const firstWord = words[0];
      if (firstWord.length >= 3 && firstWord.length <= 5) {
        suggestions.push(firstWord);
      } else {
        // Take prefixes of valid lengths (3-5)
        if (firstWord.length >= 5) {
          suggestions.push(firstWord.substring(0, 5));
        }
        if (firstWord.length >= 4) {
          suggestions.push(firstWord.substring(0, 4));
        }
        if (firstWord.length >= 3) {
          suggestions.push(firstWord.substring(0, 3));
        }
      }
    }

    // Additional Strategy: First letters of each word (if multiple words and we need more)
    if (suggestions.length < 5 && words.length >= 2) {
      const firstLetters = words.map((w) => w[0]).join('');
      if (firstLetters.length >= 3 && firstLetters.length <= 5) {
        suggestions.push(firstLetters);
      } else if (firstLetters.length > 5) {
        // If too long, take first 3-5 letters
        suggestions.push(firstLetters.substring(0, 5));
        if (firstLetters.length >= 4) {
          suggestions.push(firstLetters.substring(0, 4));
        }
        if (firstLetters.length >= 3) {
          suggestions.push(firstLetters.substring(0, 3));
        }
      }
    }

    // Remove duplicates and filter to valid format (3-5 uppercase letters)
    this.aliasSuggestions = [...new Set(suggestions)]
      .filter((s) => /^[A-Z]{3,5}$/.test(s))
      .slice(0, 5); // Limit to 5 suggestions
  }

  onAliasSuggestionSelected(suggestion: string): void {
    this.form.get('alias')?.setValue(suggestion);
    this.form.get('alias')?.markAsDirty();
  }
}

function customValidator(control: AbstractControl): { [key: string]: boolean } | null {
  const supported: Language[] = control.get('supportedLanguages').value || [];
  const defaultLanguage: Language = control.get('language').value;
  const isValid = supported.includes(defaultLanguage);
  return isValid ? null : { lang: true };
}
